import { FormikProps } from 'formik';
import Banner from '@cpg-its/ads.containers.banner';
import React from 'react';
import { useSelector } from 'react-redux';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { NumberField, NumberInputTypes } from '../../../../../global/components/form-element/number-field';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { YesNoOptions } from '../../../../../global/constants/lists';
import { labelSupplementStyles } from '../../../../../global/constants/theme/styles/components/label-supplement-styles';
import { parseLov } from '../../../../../global/helpers/form-data-helper';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { NewHireProps } from '../../../models/arc-epi-props';
import { CompensationRecordType, NewHireFormValues, StipNewHireFormValues } from '../models/new-hire-form-model';
import { FieldAutomationIds } from '../../../../../global/constants/field-automation-ids';

interface Props {
	formProps: FormikProps<NewHireFormValues>;
	newHireProps: NewHireProps;
}
export function NewHireStipEmploymentCompFields(props: Props) {
	const { formProps, newHireProps } = props;

	const positionTitles = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.position_title);
	const positionTitleOptions = positionTitles ? parseLov(positionTitles, true) : [];
	const labelClasses = labelSupplementStyles();
	const isScheduledPayment = (formProps.values as StipNewHireFormValues).record_type === CompensationRecordType.ScheduledPayment;

	function isValueNullOrZero(numberValue: any){
		return !numberValue || Number(numberValue) === 0;
	}

	const showBaseSalaryMessage = isValueNullOrZero((formProps?.values as StipNewHireFormValues).base_salary) &&
								  isValueNullOrZero((formProps?.values as StipNewHireFormValues).cash_housing_allowance) &&
								  isValueNullOrZero((formProps?.values as StipNewHireFormValues).employer_contributions);

	const recordTypeOptions = [
		{
			label: newHireProps?.schedulePaymentOptionText || 'Scheduled Payment',
			value: CompensationRecordType.ScheduledPayment,
		},
		{
			label: newHireProps?.oneTimePaymentOptionText || 'One-time Payment',
			value: CompensationRecordType.OneTimePayment,
		},
	];

	const renderScheduledPaymentFields = () => {		

		return (
			<FormGroup>
				<h4 className='divider-section-item-header'>{newHireProps?.annualIncomeAmountsHeader}</h4>
				<NumberField
					type={NumberInputTypes.Currency}
					name='base_salary'
					label={newHireProps?.baseSalaryLabel}
					formikProps={formProps}
					tooltip={newHireProps?.baseSalaryTooltip}
					testId={FieldAutomationIds.baseSalaryAmount}

				/>				
				<NumberField
					type={NumberInputTypes.Currency}
					name='employer_contributions'
					label={newHireProps?.employerContributionsLabel}
					formikProps={formProps}
					tooltip={newHireProps?.employerContributionsTooltip}
					testId={FieldAutomationIds.employerContibutions}

				/>
				<NumberField
					type={NumberInputTypes.Currency}
					name='cash_housing_allowance'
					label={newHireProps?.cashHousingAllowanceLabel}
					formikProps={formProps}
					tooltip={newHireProps?.cashHousingAllowanceTooltip}
					testId={FieldAutomationIds.cashHousingAllowance}

				/>
				{
					showBaseSalaryMessage &&
					<ContentSpacer>
						<Banner
							banner={{
								type: 'info',
								label: newHireProps?.baseSalaryInformationMessage,
							}} />
					</ContentSpacer>
				}
				<div className='form-item'>
					<h4 className='divider-section-item-header'>{newHireProps?.housingHeader}</h4>
					<FormField
						name='employer_provided_housing'
						label={newHireProps?.employerProvidedHousingLabel}
						component='radioGroup'
						options={YesNoOptions}
						touched={formProps.touched}
						errors={formProps.errors}
						tooltip={{ title: newHireProps?.employerProvidedHousingTooltip }}
						required
						testId={FieldAutomationIds.employerProviderHousingRadio}


					/>
				</div>
				<DividerSection>
					<WysiwygWithReactLink content={newHireProps?.compensationDescription} />
				</DividerSection>
			</FormGroup>
		);
	};

	const renderOneTimePaymentFields = () => {
		return (
			<NumberField
				type={NumberInputTypes.Currency}
				name='amount'
				label={newHireProps?.oneTimePaymentAmountLabel}
				formikProps={formProps}
				autoWidth
				testId={FieldAutomationIds.oneTimeSalaryAmount}
			/>
		);
	};

	const renderCompFields = () => {
		if (isScheduledPayment) {
			return renderScheduledPaymentFields();
		}

		return renderOneTimePaymentFields();
	};

	function RenderPaymentBanner() {
		const bannerContent = isScheduledPayment ? newHireProps?.scheduledPaymentTooltip : newHireProps?.oneTimePaymentTooltip;

		if (!bannerContent) return null;
		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'warning',
						description:
							<div className='rich-text'>
								<WysiwygWithReactLink content={bannerContent} />
							</div>,
					}}
				/>
			</ContentSpacer>);
	}

	function renderWarningMessage() {

		if (!newHireProps?.compensationBannerMessage) {
			return null;
		}

		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'info',
						label:
							<div className='rich-text'>
								<WysiwygWithReactLink content={newHireProps?.compensationBannerMessage} />
							</div>,
					}}
				/>
			</ContentSpacer>);
	}

	return (
		<>
			<DividerSection sectionText={newHireProps?.employmentHeader} removeDivider>
				<FormGroup>
					<FormField
						name='hire_date'
						label={newHireProps?.employmentHireDateLabel}
						component='datePicker'
						touched={formProps.touched}
						errors={formProps.errors}
						tooltip={{ title: newHireProps?.employmentHireDateTooltip }}
						required
						autoWidth
						testId={FieldAutomationIds.hiringDate}
					/>
					{renderWarningMessage()}
					<FormField
						name='position_title'
						options={positionTitleOptions}
						label={newHireProps?.positionTitleLabel}
						component='input'
						touched={formProps.touched}
						errors={formProps.errors}
						tooltip={{ title: newHireProps?.positionTitleTooltip }}
						select
						required
						testId={FieldAutomationIds.positionTitleDropdown}

					/>
					<FormField
						name='position_efective_date'
						label={newHireProps?.positionEffectiveDateLabel}
						component='datePicker'
						touched={formProps.touched}
						errors={formProps.errors}
						autoWidth
						required
						testId={FieldAutomationIds.positionEffectiveDate}
					/>
					<FormField
						name='published_title'
						label={newHireProps?.publishedTitleLabel}
						component='input'
						touched={formProps.touched}
						errors={formProps.errors}
						tooltip={{ title: newHireProps?.publishedTitleTooltip }}
						testId={FieldAutomationIds.publishedTitle}
					/>
					<NumberField
						type={NumberInputTypes.Integer}
						name='hours'
						label={newHireProps?.hoursPerYearLabel}
						formikProps={formProps}
						autoWidth
						required
						testId={FieldAutomationIds.hoursExpectedToWork}
					/>

					<ContentSpacer>
						<p>{newHireProps?.perYearText}</p>
					</ContentSpacer>

					<FormField
						name='scheduled_hours_effective_date'
						label={newHireProps?.scheduledHoursEffectiveDateLabel}
						component='datePicker'
						touched={formProps.touched}
						errors={formProps.errors}
						required
						autoWidth
						testId={FieldAutomationIds.scheduleHoursEffectiveDate}
					/>
				</FormGroup>
			</DividerSection>
			<DividerSection sectionText={newHireProps?.compensationHeader} removeDivider>
				<FormGroup>
					<ContentSpacer>
						<FormField
							name='record_type'
							component='radioGroup'
							label={newHireProps?.recordTypeLabel}
							options={recordTypeOptions}
							touched={formProps.touched}
							errors={formProps.errors}
							required
							testId={FieldAutomationIds.compensationTypeRadio}

						/>
					</ContentSpacer>
					<ContentSpacer>
						<div className={`${labelClasses.labelLayout} divider-section-item-header`}>
							<h4>{isScheduledPayment ? newHireProps?.scheduledPaymentHeader : newHireProps?.oneTimePaymentHeader}</h4>
						</div>
						{
							newHireProps?.scheduledPaymentHeaderMessage &&
							<ContentSpacer> 
								<WysiwygWithReactLink content={newHireProps?.scheduledPaymentHeaderMessage} />							
							</ContentSpacer>
						}		
						<RenderPaymentBanner />
						<FormField
							name='compensation_effective_date'
							label={newHireProps?.compEffectiveDateLabel}
							component='datePicker'
							touched={formProps.touched}
							errors={formProps.errors}
							required
							testId={FieldAutomationIds.effectiveDateOfCompensation}
						/>				
					</ContentSpacer>
				</FormGroup>
				{renderCompFields()}
			</DividerSection>
		</>
	);
}